import { updateHolePlacement } from './HolePlacementHelper';
import { rebuildClipPath } from './CanvasFormService';
import { mmToPixels, pixelsToMM } from './Viewport/Units';
export { DPI, mmToPixels, pixelsToMM } from './Viewport/Units';
export const canvasPixelsToMM = (pixels) => {
    return pixelsToMM(pixels);
};
export const mmToCanvasPixels = (mm) => {
    return mmToPixels(mm);
};
export const sizes = {
    A1: { height: 841, width: 594 },
    A2: { height: 594, width: 420 },
    A3: { height: 420, width: 297 },
    A4: { height: 297, width: 210 },
    A5: { height: 210, width: 148 },
    '200x200': { height: 200, width: 200 },
    Custom: { height: 210, width: 148 },
};
/**
 * Applies a new sign size in scene space: rescales objects, rebuilds the clip path
 * and repositions holes. It does not touch the canvas element or the camera - use
 * CanvasViewport for that, so pan and zoom survive a size change.
 */
export const handleSizeChange = (canvasSize, fabricCanvas, selectedHoles = 'none', renderAll = true, moveObjects = true) => {
    const oldCanvasSize = fabricCanvas.get('canvasSize');
    const oldRealWidthPx = oldCanvasSize
        ? mmToPixels(oldCanvasSize.width)
        : mmToPixels(canvasSize.width);
    const oldRealHeightPx = oldCanvasSize
        ? mmToPixels(oldCanvasSize.height)
        : mmToPixels(canvasSize.height);
    const widthRatio = mmToPixels(canvasSize.width) / oldRealWidthPx;
    const heightRatio = mmToPixels(canvasSize.height) / oldRealHeightPx;
    // Reads the old size off the canvas, so it has to run before canvasSize is replaced.
    if (oldCanvasSize && moveObjects && (widthRatio !== 1 || heightRatio !== 1)) {
        scaleObjectsToNewCanvasSize(fabricCanvas, widthRatio, heightRatio);
    }
    fabricCanvas.set({ canvasSize: canvasSize });
    rebuildClipPath(fabricCanvas);
    if (selectedHoles !== 'none') {
        updateHolePlacement(fabricCanvas, selectedHoles);
    }
    if (renderAll) {
        fabricCanvas.renderAll();
    }
};
export const updateMeasurementPositions = (mmHeight, mmWidth, fabricRef) => {
    const left = document.getElementById('measurementLeft');
    const bottom = document.getElementById('measurementBottom');
    if (!left || !bottom)
        return;
    left.textContent = `${mmHeight} mm`;
    bottom.textContent = `${mmWidth} mm`;
};
export const debounce = (func, delay) => {
    let timeoutId = null;
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(...args);
            timeoutId = null;
        }, delay);
    };
};
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
const isText = (o) => (o === null || o === void 0 ? void 0 : o.type) === 'textbox' || (o === null || o === void 0 ? void 0 : o.type) === 'i-text' || (o === null || o === void 0 ? void 0 : o.type) === 'text';
function resizeTextUniform(textObj, uniform) {
    const originalFontSize = textObj.fontSize || 16;
    const newFontSize = clamp(Math.round(originalFontSize * uniform), 8, 2000);
    const origWidth = textObj.width || 0;
    const newWidth = origWidth * (newFontSize / originalFontSize);
    textObj.set({
        fontSize: newFontSize,
        width: newWidth,
        scaleX: 1,
        scaleY: 1,
    });
    if (typeof textObj.initDimensions === 'function')
        textObj.initDimensions();
    return { bw: textObj.width || 0, bh: textObj.height || 0 };
}
function detectAnchors(left, top, bw, bh, oldW, oldH, thresholdPx) {
    const distLeft = left;
    const distRight = oldW - (left + bw);
    const distTop = top;
    const distBottom = oldH - (top + bh);
    let ax;
    if (distLeft <= thresholdPx)
        ax = 'left';
    else if (distRight <= thresholdPx)
        ax = 'right';
    else
        ax = 'center';
    let ay;
    if (distTop <= thresholdPx)
        ay = 'top';
    else if (distBottom <= thresholdPx)
        ay = 'bottom';
    else
        ay = 'middle';
    return { ax, ay, distLeft, distRight, distTop, distBottom };
}
export const scaleObjectsToNewCanvasSize = (fabricCanvas, widthRatio, heightRatio) => {
    const oldSize = fabricCanvas.get('canvasSize');
    const oldW = mmToPixels(oldSize.width);
    const oldH = mmToPixels(oldSize.height);
    const newW = oldW * widthRatio;
    const newH = oldH * heightRatio;
    const ANCHOR_THRESH_PX = mmToPixels(5);
    fabricCanvas.getObjects().forEach((obj) => {
        if ([
            'watermark',
            'watermark-pattern',
            'border-for-path',
            'hole-group',
        ].includes(obj.id || '')) {
            return;
        }
        const curLeft = obj.left || 0;
        const curTop = obj.top || 0;
        const curSX = obj.scaleX || 1;
        const curSY = obj.scaleY || 1;
        const baseW = obj.width || 0;
        const baseH = obj.height || 0;
        const bw = baseW * curSX;
        const bh = baseH * curSY;
        const { ax, ay, distLeft, distRight, distTop, distBottom } = detectAnchors(curLeft, curTop, bw, bh, oldW, oldH, ANCHOR_THRESH_PX);
        let newBW = bw;
        let newBH = bh;
        if (isText(obj)) {
            const uniform = heightRatio;
            const after = resizeTextUniform(obj, uniform);
            newBW = after.bw;
            newBH = after.bh;
        }
        else if (obj.id === 'icon' ||
            obj.type === 'image' ||
            obj.type === 'path' ||
            obj.type === 'group') {
            const newSX = curSY * heightRatio;
            const newSY = curSY * heightRatio;
            obj.set({ scaleX: newSX, scaleY: newSY });
            newBW = (obj.width || 0) * newSX;
            newBH = (obj.height || 0) * newSY;
        }
        else {
            const newSX = curSX * widthRatio;
            const newSY = curSY * heightRatio;
            obj.set({ scaleX: newSX, scaleY: newSY });
            newBW = (obj.width || 0) * newSX;
            newBH = (obj.height || 0) * newSY;
        }
        let newLeft;
        if (ax === 'left') {
            newLeft = distLeft * widthRatio;
        }
        else if (ax === 'right') {
            const newRight = distRight * widthRatio;
            newLeft = newW - newRight - newBW;
        }
        else {
            const centerOffsetX = curLeft + bw / 2 - oldW / 2;
            const newCenterX = (oldW / 2 + centerOffsetX) * widthRatio;
            newLeft = newCenterX - newBW / 2;
        }
        let newTop;
        if (ay === 'top') {
            newTop = distTop * heightRatio;
        }
        else if (ay === 'bottom') {
            const newBottom = distBottom * heightRatio;
            newTop = newH - newBottom - newBH;
        }
        else {
            const centerOffsetY = curTop + bh / 2 - oldH / 2;
            const newCenterY = (oldH / 2 + centerOffsetY) * heightRatio;
            newTop = newCenterY - newBH / 2;
        }
        obj.set({ left: newLeft, top: newTop });
        obj.setCoords();
    });
    fabricCanvas.renderAll();
};
